import { groupBy } from '@sparelabs/core'
import { pick } from 'lodash'
import {
  IAbsoluteTimeConstraint,
  IRelativeTimeConstraint,
  IWindow,
  OffsetDirection,
  TimeConstraintType,
} from '../../scheduling/TimeConstraintTypes'
import { SlotType } from '../../types'
import {
  ConstraintOverrideType,
  IAbsoluteConstraintOverride,
  IDutyEndConstraintOverride,
  INewConstraintOverride,
  INewRequestOverride,
  IRelativeConstraintOverride,
  IRequestConstraintOverride,
  ITimeInVehicleOverride,
  IWindowOverride,
} from './ConstraintOverrideTypes'

/**
 * We have changed how we represent request overrides when we interact with ROS,
 * but are keeping for now the legacy representations in two places:
 * - The RequestConstraintTable: because we only need for this for admin.
 *   so we can postpone making that change.
 * - The constraintOverrides property in EstimateResult because we have past data
 *   in the old representation that we are not current migrating.
 */
export class ConstraintOverridesConverter {
  /**
   * We use this method to convert new request overrides to legacy request overrides
   * for saving in the RequestConstraintTable.
   */
  public static toLegacyRequestOverrides(
    requestOverride: INewRequestOverride,
    unmatchedPickupWindow: IWindow | null,
    unmatchedDropoffWindow: IWindow | null,
  ): {
    pickupOverride: IRequestConstraintOverride | null
    dropoffOverride: IRequestConstraintOverride | null
  } {
    const pickupOverride = unmatchedPickupWindow
      ? this.toAbsoluteConstraintOverride(requestOverride.requestId, SlotType.Pickup, requestOverride.pickupOverride)
      : this.toRelativeConstraintOverride(
          requestOverride.requestId,
          SlotType.Pickup,
          requestOverride.timeInVehicleOverride,
          OffsetDirection.Before,
        )

    const dropoffOverride = unmatchedDropoffWindow
      ? this.toAbsoluteConstraintOverride(requestOverride.requestId, SlotType.Dropoff, requestOverride.dropoffOverride)
      : this.toRelativeConstraintOverride(
          requestOverride.requestId,
          SlotType.Dropoff,
          requestOverride.timeInVehicleOverride,
          OffsetDirection.After,
        )

    return {
      pickupOverride,
      dropoffOverride,
    }
  }

  private static toAbsoluteConstraintOverride(
    requestId: string,
    slotType: SlotType.Pickup | SlotType.Dropoff,
    windowOverride: IWindowOverride | undefined,
  ): IRequestConstraintOverride | null {
    if (!windowOverride) {
      return null
    }

    const constraint: IAbsoluteTimeConstraint = {
      type: TimeConstraintType.Absolute,
      ...windowOverride.constraint,
    }

    return {
      type: ConstraintOverrideType.AbsoluteRequest,
      slotType,
      requestId,
      scheduledTs: windowOverride.scheduledTs,
      constraint,
    }
  }

  private static toRelativeConstraintOverride(
    requestId: string,
    slotType: SlotType.Pickup | SlotType.Dropoff,
    timeInVehicleOverride: ITimeInVehicleOverride | undefined,
    direction: OffsetDirection,
  ): IRequestConstraintOverride | null {
    if (!timeInVehicleOverride) {
      return null
    }

    const constraint: IRelativeTimeConstraint = {
      type: TimeConstraintType.Relative,
      maxOffset: timeInVehicleOverride.maxTimeInVehicle,
      direction,
    }

    return {
      type: ConstraintOverrideType.RelativeRequest,
      slotType,
      requestId,
      newTimeInVehicle: timeInVehicleOverride.timeInVehicle,
      constraint,
    }
  }

  /**
   * We use this method when retrieving overrides from the `constraintOverrides` property in `EstimateResult`.
   * This is json field property which includes:
   * - request overrides in the format currently used
   * - duty overrides in the format currently used
   * - legacy request overrides in a now discontinued format (absolute and relative)
   * For backwards compatibility, we need to support the legacy format and convert it to the new one.
   */
  public static fromStoredOverrides(constraintOverrides: Array<INewConstraintOverride | IRequestConstraintOverride>): {
    requestOverrides: INewRequestOverride[]
    dutyOverrides: IDutyEndConstraintOverride[]
  } {
    const { requestOverrides, dutyOverrides, legacyRequestOverrides } =
      this.partitionConstraintOverrides(constraintOverrides)

    const fromLegacyRequestOverrides = this.legacyToRequestOverrides(legacyRequestOverrides)

    return {
      requestOverrides: [...requestOverrides, ...fromLegacyRequestOverrides],
      dutyOverrides,
    }
  }

  private static partitionConstraintOverrides(
    constraintOverrides: Array<INewConstraintOverride | IRequestConstraintOverride>,
  ): {
    requestOverrides: INewRequestOverride[]
    dutyOverrides: IDutyEndConstraintOverride[]
    legacyRequestOverrides: IRequestConstraintOverride[]
  } {
    const requestOverrides: INewRequestOverride[] = []
    const dutyOverrides: IDutyEndConstraintOverride[] = []
    const legacyRequestOverrides: IRequestConstraintOverride[] = []

    for (const constraintOverride of constraintOverrides) {
      switch (constraintOverride.type) {
        case ConstraintOverrideType.Request:
          requestOverrides.push(constraintOverride)
          break
        case ConstraintOverrideType.DutyEnd:
          dutyOverrides.push(constraintOverride)
          break
        case ConstraintOverrideType.AbsoluteRequest:
        case ConstraintOverrideType.RelativeRequest:
          legacyRequestOverrides.push(constraintOverride)
      }
    }

    return {
      requestOverrides,
      dutyOverrides,
      legacyRequestOverrides,
    }
  }

  private static legacyToRequestOverrides(legacyRequestOverrides: IRequestConstraintOverride[]): INewRequestOverride[] {
    const requestIdToOverrides = groupBy(legacyRequestOverrides, (override) => override.requestId)
    const requestOverrides: INewRequestOverride[] = []

    for (const [requestId, overrides] of requestIdToOverrides.entries()) {
      let absolutePickupOverride: IAbsoluteConstraintOverride | null = null
      let absoluteDropoffOverride: IAbsoluteConstraintOverride | null = null
      let relativePickupOverride: IRelativeConstraintOverride | null = null
      let relativeDropoffOverride: IRelativeConstraintOverride | null = null

      for (const override of overrides) {
        if (override.slotType === SlotType.Pickup && override.type === ConstraintOverrideType.AbsoluteRequest) {
          absolutePickupOverride = override
        } else if (override.slotType === SlotType.Dropoff && override.type === ConstraintOverrideType.AbsoluteRequest) {
          absoluteDropoffOverride = override
        } else if (override.slotType === SlotType.Pickup && override.type === ConstraintOverrideType.RelativeRequest) {
          relativePickupOverride = override
        } else if (override.slotType === SlotType.Dropoff && override.type === ConstraintOverrideType.RelativeRequest) {
          relativeDropoffOverride = override
        }
      }

      const pickupOverride: IWindowOverride | undefined = absolutePickupOverride
        ? {
            scheduledTs: absolutePickupOverride.scheduledTs,
            constraint: pick(absolutePickupOverride.constraint, ['min', 'desired', 'max']),
          }
        : undefined

      const dropoffOverride: IWindowOverride | undefined = absoluteDropoffOverride
        ? {
            scheduledTs: absoluteDropoffOverride.scheduledTs,
            constraint: pick(absoluteDropoffOverride.constraint, ['min', 'desired', 'max']),
          }
        : undefined

      const timeInVehicleOverride: ITimeInVehicleOverride | undefined =
        relativePickupOverride || relativeDropoffOverride
          ? {
              timeInVehicle: relativePickupOverride?.newTimeInVehicle ?? relativeDropoffOverride?.newTimeInVehicle ?? 0,
              maxTimeInVehicle:
                relativePickupOverride?.constraint.maxOffset ?? relativeDropoffOverride?.constraint.maxOffset ?? 0,
            }
          : undefined

      requestOverrides.push({
        type: ConstraintOverrideType.Request,
        requestId,
        pickupOverride,
        dropoffOverride,
        timeInVehicleOverride,
      })
    }

    return requestOverrides
  }

  /**
   * When we save overrides applied to an estimate we do not have an available requestId yet.
   * So we save its estimateId in the requestId field. When we retrieve overrides we thus need to
   * replace any matches to the estimateId with the actual requestId.
   */
  public static replaceEstimateIdWithRequestIdForRequestToMatch(
    constraintOverrides: INewRequestOverride[],
    estimateId: string,
    requestId: string,
  ): INewRequestOverride[] {
    return constraintOverrides.map((override) =>
      override.requestId === estimateId
        ? {
            ...override,
            requestId,
          }
        : override,
    )
  }
}
